import { z } from "zod";
import { DataOrigin } from "./radius.js";
/** bb.event types, v0. Desks (S8) subscribe to these; dotted `subject.verb`.
 *  Add here first, then emit — an event type that is not in this list is a typo. */
export const EVENT_TYPES = [
    "appointment.requested",
    "appointment.check_requested",
    "appointment.applied",
    "opportunity.started",
    "opportunity.updated",
    "opportunity.plan_reserved",
    "opportunity.rooms_traced",
    "work.record_started",
    "work.revision_saved",
    "work.plan_trace_requested", // automatic plan trace queued, re-queued or cancelled (18 Oct 2026)
    "work.stage_changed",
    "proposal.accepted",
    "installer.saved",
    // The crew's own schedule address was minted, rotated or revoked.
    "installer.calendar",
    "installer.member_requested",
    "installer.member_activated",
    "installer.member_status_changed",
    "installer.workers_assigned",
    "installer.invitation_queued",
    "installer.company_reclassified",
    "installer.price_book_submitted",
    "installer.price_book_approved",
    "sales_need.visibility_changed",
    "lead.created",
    "lead.work_linked",
    "lead.response_changed",
    "lead.trial_quarantined",
    "lead.dispositioned",
    // S2: the free onsite measure — a personal booking link minted, a measure booked or cancelled.
    "lead.booking_link_created",
    "lead.measure_booked",
    "lead.measure_cancelled",
    "touch.recorded",
    "follow_up.scheduled",
    "proposal.updated",
    "follow_up.completed",
    "contact.resolved",
    "contact.created",
    "contact.updated",
    "contact.account_changed",
    "account.created",
    "account.updated",
    "job.created",
    "job.stage_changed",
    "job.updated",
    // The personal-job exception: this job bills the person, not the account.
    // Distinct from `job.updated` because it moves money and a desk that cares
    // about receivables should hear it specifically.
    "job.billing_changed",
    "job.skirting_recorded",
    "job.skirting_link_changed",
    "job.skirting_confirmed",
    "job.skirting_guide_drafted",
    "job.architrave_guide_drafted",
    "job.architrave_confirmed",
    // A prepared document (a colour sheet, a strata pack) was kept under the Job and noted on its timeline.
    "job.document_recorded",
    // The customer's private job page: its link was minted or revoked; the customer recorded a choice on it.
    "job.page_link_changed",
    "job.page_choice",
    "job.page_message",
    // EmmA on the customer's Proposal page, and the language Artemis set for a customer (14 Oct 2026).
    "proposal.page_message",
    "proposal.page_learning_approved", // 17 Oct 2026: staff let EmmA look something up for a customer
    "contact.language_set",
    "note.added",
    "quote.created",
    "offer.drafted",
    "offer.issued",
    "offer.responded",
    // S2b: the two quote fixes EmmA already had as tools. `primary_set` is not a
    // `quote.created` — the job's deal value moves and nothing was created, and a
    // desk that wanted one of those would be wrong to hear the other.
    "quote.primary_set",
    "quote.removed",
    "install.scheduled",
    "install.rescheduled",
    "install.day_state_changed",
    "wo.completed",
    "home.measured",
    "spend.recorded",
    // S4 attribution: a campaign is the row spend and attributed leads both hang on.
    "campaign.set",
    "config.changed",
    // Catalogue: a cost change moves money on future quotes, and retiring a
    // product takes it off the website, so both are worth their own event rather
    // than riding on config.changed.
    "catalogue.documents_set",
    "catalogue.price_set",
    "catalogue.product_retired",
    // Price my floor: listing a product publishes its installed price on the website.
    "catalogue.price_my_floor_changed",
    // Price my floor inline edits: the list price, and a separately priced installation method.
    "catalogue.sell_price_set",
    "catalogue.installation_price_set",
    "case.opened",
    "case.decided",
    // S11 supply + inventory (additive, contracts stay 0.2.x)
    "po.drafted",
    "po.revised",
    "po.cancelled",
    "po.amended", // 15 Oct 2026: a sent order's lines re-issued as the next supplier version, supplier emailed the diff
    "po.assigned",
    "po.sent",
    "po.delivery_queued",
    "po.delivery_cancelled",
    "po.delivery_check_requested",
    "po.acknowledged",
    "po.evidence_recorded",
    "delivery.received",
    "delivery.corrected",
    "stock.allocated",
    "stock.released",
    "stock.adjusted",
    "supplier.terms_set",
    // Bug / Improvement from the CRM header (12 October 2026): a report about the system itself.
    "feedback.raised",
    "feedback.updated",
    // The supplier hub's people and WeChat channels (16 Oct 2026).
    "supplier.contact_saved", "supplier.contact_archived", "supplier.channel_linked", "supplier.channel_unlinked",
    "supplier.promotion_recorded", "supplier.promotion_ended",
    // Files on every Opportunity and Job (18 Oct 2026): a file reserved for upload, its note, hidden or shown again.
    "record_file.reserved", "record_file.note_set", "record_file.hidden", "record_file.restored",
    // Supplier price lists and AIW's own accessory matches (23 Oct 2026).
    "supplier.price_list_recorded", "catalogue.accessory_match_saved", "catalogue.accessory_match_retired",
    "supplier.portal_read_recorded", // carpet supplier B2B portals (26 Oct 2026)
    // Installer scope sheet and the crew's WeChat group (24 Oct 2026).
    "installer.channel_linked", "installer.channel_unlinked", "installer.scope_sent", "installer.scope_answered", "installer.scope_approved",
    "installer.pay_link_sent", // the crew payment link (26 Oct 2026)
    // The AI agent account Jacky authorizes and anyone on staff may disable (ADR-034, 25 Oct 2026).
    "agent_account.authorized", "agent_account.disabled",
    // Our projects — the website showcase of past installations (25 Oct 2026).
    "showcase.project_started", "showcase.project_updated", "showcase.photo_added", "showcase.photo_removed", "showcase.photo_edited", "showcase.retouch_proposed", "showcase.retouch_decided", "showcase.project_released", "showcase.project_taken_down",
    // Supplier statements and bills: EmmA reads them, Jacky approves the Xero bills (4 Nov 2026).
    "supplier.documents_recorded", "supplier.document_held", "supplier.bills_created", "supplier.bills_synced", "supplier.bill_voided",
];
export const EventType = z.enum(EVENT_TYPES);
export const Event = z.object({
    /** opaque, ordered cursor (bb.event bigserial as text) */
    id: z.string().min(1),
    at: z.string().datetime(),
    type: EventType,
    /** the bb.action that caused it; null for synced events from aiw.outbox */
    action_id: z.string().uuid().nullable(),
    subject: z.object({ kind: z.string(), id: z.string() }),
    payload: z.record(z.string(), z.unknown()),
    data_origin: DataOrigin,
    test_set_id: z.string().uuid().nullable(),
});
/** `GET /events?since=<cursor>` page shape. */
export const EventPage = z.object({
    events: z.array(Event),
    next: z.string().nullable(),
});
//# sourceMappingURL=event.js.map